import { useEffect, useId, useState } from 'react';
import { type TranslationKey } from '@icebreaker/shared/i18n';
import {
  selectClaimById,
  selectClaimDetail,
  selectClaimMutation,
} from '@/features/claims/store/claims.selectors';
import {
  clearMutationState,
  decideClaim,
  fetchClaim,
  requestClaimInfo,
} from '@/features/claims/store/claims.slice';
import { ClaimStatusTag } from '@/features/claims/ui/claim-status-tag';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { Avatar, Button, ErrorBox, Skeleton } from '@icebreaker/ui';
import { Field, Textarea } from '@icebreaker/ui';
import styles from './admin.module.css';

/** decideClaimSchema.note zorunlu ve en az 10 karakter — arayüz 400'e düşürmez. */
const MIN_NOTE_LENGTH = 10;

/**
 * Sahiplenme karar panosu (Blok 6 dikişi — tasarım ekran 26 sağ pano).
 * Kuyruktaki claim kalemi seçilince talep ayrıntısı (talep eden, kanıtlar, gölge
 * sahibinin onay/itiraz sinyali, pencere durumu) gösterilir; onayla / reddet /
 * ek bilgi iste eylemleri `/claims/:id/decide` + `request-info` uçlarına bağlıdır.
 * Karar claim durum makinesinde verilir; kuyruk kalemi sunucuda AYNI
 * transaction'da kapanır, başarı sonrası saga kalemi ve KPI'ları tazeler.
 */
export function ClaimDecisionPanel({
  claimId,
  queueItemId,
}: {
  claimId: string;
  queueItemId: string;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const claim = useAppSelector(selectClaimById(claimId));
  const detail = useAppSelector(selectClaimDetail);
  const mutation = useAppSelector(selectClaimMutation);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [lastAction, setLastAction] = useState<'decide' | 'info' | null>(null);
  const noteId = useId();

  useEffect(() => {
    dispatch(clearMutationState());
    setNote('');
    setTouched(false);
    setLastAction(null);
    dispatch(fetchClaim.request(claimId));
  }, [dispatch, claimId]);

  // Eylem başarılı → gerekçe alanı sıfırlanır (karar panosu deseniyle aynı).
  useEffect(() => {
    if (mutation.phase === 'succeeded') {
      setNote('');
      setTouched(false);
    }
  }, [mutation.phase]);

  if (!claim) {
    if (detail.phase === 'failed') {
      return (
        <div className={styles.errorBox} role="alert">
          {t.t('admin.moderation.claimPanel.loadError')}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => dispatch(fetchClaim.request(claimId))}
          >
            {t.t('common.retry')}
          </Button>
        </div>
      );
    }
    return <Skeleton label={t.t('a11y.loadingContent')} lines={6} lineHeight={18} />;
  }

  const trimmed = note.trim();
  const tooShort = trimmed.length < MIN_NOTE_LENGTH;
  // Karar yalnız bu iki durumda verilebilir (durum makinesi — Blok 1).
  const decidable = claim.status === 'objection_window' || claim.status === 'objected';
  const windowOpen =
    claim.objectionWindowEndsAt !== null &&
    Date.now() < new Date(claim.objectionWindowEndsAt).getTime();

  const decide = (decision: 'approved' | 'rejected') => {
    setTouched(true);
    setLastAction('decide');
    if (tooShort) return;
    dispatch(decideClaim.request({ claimId, queueItemId, input: { decision, note: trimmed } }));
  };
  const askInfo = () => {
    setTouched(true);
    setLastAction('info');
    if (tooShort) return;
    dispatch(requestClaimInfo.request({ claimId, queueItemId, input: { message: trimmed } }));
  };

  return (
    <>
      {/* İnsan öğesi: kurum köşeli, kişi daire avatar (README 4.6). */}
      <div className={styles.partyRow}>
        <Avatar
          name={claim.claimantDisplayName}
          size="md"
          shape={claim.claimantOrganizationName ? 'entity' : 'person'}
        />
        <span className={styles.partyMeta}>
          <span className={styles.partyLabel}>{t.t('admin.moderation.claimPanel.claimant')}</span>
          <span className={styles.partyName}>{claim.claimantDisplayName}</span>
        </span>
        <ClaimStatusTag status={claim.status} />
      </div>
      <div className={styles.partyRow}>
        <Avatar name={claim.founderDisplayName} size="md" shape="person" />
        <span className={styles.partyMeta}>
          <span className={styles.partyLabel}>{t.t('admin.moderation.claimPanel.founder')}</span>
          <span className={styles.partyName}>{claim.founderDisplayName}</span>
        </span>
      </div>

      <div className={styles.detailKicker} style={{ marginTop: 'var(--ib-space-4)' }}>
        {t.t('admin.moderation.claimPanel.reason')}
      </div>
      <div className={styles.detailBody}>{claim.reason}</div>

      {claim.evidences.length > 0 ? (
        <>
          <div className={styles.detailKicker} style={{ marginTop: 'var(--ib-space-4)' }}>
            {t.t('admin.moderation.claimPanel.evidences')}
          </div>
          <div className={styles.factList} style={{ marginTop: 'var(--ib-space-2)' }}>
            {claim.evidences.map((evidence) => (
              <div key={evidence.id} className={styles.factRow}>
                <span className={styles.factKey}>
                  {t.t(
                    `admin.moderation.claimPanel.evidenceTypes.${evidence.type}` as TranslationKey,
                  )}
                </span>
                <span className={styles.factValue}>{evidence.value ?? '—'}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* Sahip sinyali: itiraz kırmızı, açık onay yeşil, bekleyen pencere kehribar. */}
      {claim.status === 'objected' ? (
        <div className={`${styles.stateBox} ${styles.stateObjection}`}>
          {t.t('admin.moderation.claimPanel.objected', {
            date: claim.objectedAt ? t.formatDate(claim.objectedAt) : '—',
            reason: claim.objectionReason ?? '—',
          })}
        </div>
      ) : null}
      {claim.ownerConsentedAt ? (
        <div className={`${styles.stateBox} ${styles.stateConsent}`}>
          {t.t('admin.moderation.claimPanel.consented', {
            date: t.formatDate(claim.ownerConsentedAt),
          })}
        </div>
      ) : null}
      {claim.status === 'objection_window' && !claim.ownerConsentedAt ? (
        <div
          className={windowOpen ? `${styles.stateBox} ${styles.statePending}` : styles.decidedNote}
        >
          {t.t(
            windowOpen
              ? 'admin.moderation.claimPanel.windowOpen'
              : 'admin.moderation.claimPanel.windowEnded',
            {
              date: claim.objectionWindowEndsAt ? t.formatDate(claim.objectionWindowEndsAt) : '—',
            },
          )}
        </div>
      ) : null}

      {decidable ? (
        <>
          <div className={styles.decisionField}>
            <Field
              id={noteId}
              label={t.t('admin.moderation.decisionLabel')}
              error={
                touched && tooShort ? t.t('admin.moderation.claimPanel.noteTooShort') : undefined
              }
            >
              <Textarea
                id={noteId}
                rows={3}
                value={note}
                placeholder={t.t('admin.moderation.decisionPlaceholder')}
                invalid={touched && tooShort}
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
          </div>
          {/* 409 CLAIM_OBJECTION_WINDOW_OPEN dahil: kod anlamlı i18n metnine çözülür. */}
          <ErrorBox error={mutation.error} className={styles.errorBox} />
          {mutation.phase === 'succeeded' && lastAction === 'info' ? (
            <div className={`${styles.stateBox} ${styles.stateConsent}`} role="status">
              {t.t('admin.moderation.claimPanel.infoRequestedSent')}
            </div>
          ) : null}
          <div className={styles.decisionActions}>
            <Button
              variant="primary"
              loading={mutation.phase === 'loading' && lastAction === 'decide'}
              loadingLabel={t.t('admin.moderation.deciding')}
              onClick={() => decide('approved')}
            >
              {t.t('admin.moderation.approve')}
            </Button>
            <Button
              variant="dangerOutline"
              disabled={mutation.phase === 'loading'}
              onClick={() => decide('rejected')}
            >
              {t.t('admin.moderation.reject')}
            </Button>
            <Button variant="secondary" disabled={mutation.phase === 'loading'} onClick={askInfo}>
              {t.t('admin.moderation.requestInfo')}
            </Button>
          </div>
          <div className={styles.auditNote}>{t.t('admin.moderation.auditNote')}</div>
        </>
      ) : (
        <p className={styles.decidedNote}>
          {claim.decisionNote ? `${t.t('claims.decisionNoteLabel')}: ${claim.decisionNote}` : null}
        </p>
      )}
    </>
  );
}
